/* eslint-disable @typescript-eslint/no-unused-vars */

import { useLazyQuery, useMutation } from '@apollo/client'
import { Button, Divider, Typography } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import DoneIcon from '@material-ui/icons/Done'
import { Skeleton } from '@material-ui/lab'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import {
  PMS_EVENT_DONE_MUTATION,
  PMS_EVENT_RETRY_LEVEL_MUTATION,
  PMS_EVENTS_QUERY
} from '../../../../graphql/PMSEvents'
import { gregorianToJalaaliWithHourMinuteAndSecond } from '../../../../helpers/date'
import { getRoleFromStorage } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import bookRoleManagement from '../../../../staticRoleManagement/bookRoleManagement'
import { PMSEvent } from '../../../../types/PMSEvents'
import { BookingDetail } from '../../../../types/bookingDetails'
import { useStyles } from './styles/index.styles'

type PMSEventsProps = {
  data: BookingDetail | undefined
  originalData: BookingDetail | undefined
}

export default function PMSEvents({ data, originalData }: PMSEventsProps) {
  const classes = useStyles()
  const [
    getPmsEventData,
    { data: PmsEvent, loading: loadingForGetPMSEvents, refetch }
  ] = useLazyQuery<{
    PmsEvent: PMSEvent[]
  }>(PMS_EVENTS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [retryFromThisLevelMutateFunction, { loading: loadingForRetry }] =
    useMutation(PMS_EVENT_RETRY_LEVEL_MUTATION, {
      notifyOnNetworkStatusChange: true
    })

  const retryFromThisLevelFunc = (index: number) => {
    retryFromThisLevelMutateFunction({
      variables: {
        PmsEventRetryLevelInput: {
          bookingId: PmsEvent?.PmsEvent[index].bid,
          pmsId: PmsEvent?.PmsEvent[index].id,
          event: PmsEvent?.PmsEvent[index].event,
          payload: PmsEvent?.PmsEvent[index].payload
        }
      }
    })
      .then(({ data: response }) => {
        toast.success(response.PmsEventRetryLevel.message)
      })
      .catch((response) => {
        toast.error(response.message)
      })
  }

  const [doneFunction, { loading: loadingForDone }] = useMutation(
    PMS_EVENT_DONE_MUTATION,
    {
      notifyOnNetworkStatusChange: true
    }
  )
  const doneFunc = (index: number) => {
    doneFunction({
      variables: {
        PmsEventDoneInput: {
          bookingId: PmsEvent?.PmsEvent[index].bid,
          event: PmsEvent?.PmsEvent[index].event,
          eventId: PmsEvent?.PmsEvent[index].id,
          payload: PmsEvent?.PmsEvent[index].payload
        }
      }
    })
      .then(({ data: response }) => {
        refetch()
        toast.success(response.PmsEventDone.message)
      })
      .catch((response) => {
        toast.error(response.message)
      })
  }

  const RoleAccessToGetPMSEvent = bookRoleManagement
    .find((item) => item.route === '/booking/list/:reserveNumber/view')
    ?.sections?.GET_PMS_EVENT.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )

  useEffect(() => {
    if (originalData?.basicDetails.id && RoleAccessToGetPMSEvent) {
      getPmsEventData({
        variables: {
          bookingID: originalData?.basicDetails.id
        }
      })
    }
  }, [originalData?.basicDetails.id])

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <Typography variant="h4" className={classes.blackText}>
          {getTranslate('PMS Events')}
        </Typography>
      </div>
      <Divider />
      <div className={classes.bottom}>
        <div className={classes.table}>
          <div className={classes.headerRow}>
            <Typography variant="h6" className={classes.cell}>
              {getTranslate('Version')}
            </Typography>
            <Typography variant="h6" className={classes.cell}>
              {getTranslate('Event')}
            </Typography>
            <Typography variant="h6" className={classes.cell}>
              {getTranslate('Is Done')}
            </Typography>
            <Typography variant="h6" className={classes.cell}>
              {getTranslate('Done At')}
            </Typography>
          </div>
          {data && !loadingForGetPMSEvents ? (
            <>
              {PmsEvent?.PmsEvent?.map((item, index) => (
                <div key={index} className={classes.row}>
                  <Typography variant="h6" className={classes.cell}>
                    {item.version}
                  </Typography>
                  <Typography variant="h6" className={classes.cell}>
                    {item.version}
                  </Typography>
                  <Typography variant="body1" className={classes.cell}>
                    {!!item.done ? (
                      <DoneIcon color="secondary" />
                    ) : (
                      <CloseIcon color="error" />
                    )}
                  </Typography>
                  <Typography variant="h6" className={classes.cell}>
                    {lang === 'fa'
                      ? gregorianToJalaaliWithHourMinuteAndSecond(item.doneAt)
                      : item.doneAt}
                  </Typography>
                  <div className={classes.buttons}>
                    <Typography variant="h6" className={classes.doneButton}>
                      <Button
                        onClick={() => doneFunc(index)}
                        disabled={item.done || loadingForDone}
                        color="primary"
                        variant="outlined">
                        {getTranslate('DONE')}
                      </Button>
                    </Typography>
                    <Typography variant="h6">
                      <Button
                        onClick={() => retryFromThisLevelFunc(index)}
                        disabled={item.done || loadingForRetry}
                        color="primary"
                        variant="contained">
                        {getTranslate('RETRY FROM THIS LEVEL')}
                      </Button>
                    </Typography>
                  </div>
                </div>
              ))}
            </>
          ) : (
            Array(4)
              .fill('')
              .map((_, index) => (
                <div key={index}>
                  <div className={classes.row}>
                    <Skeleton animation="wave" />
                    <Skeleton animation="wave" />
                    <Skeleton animation="wave" />
                    <Skeleton animation="wave" />
                    <Skeleton animation="wave" />
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  )
}
